import { useState } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Check, Copy } from 'lucide-react'

interface CodeBlockProps {
  inline?: boolean
  className?: string
  children?: React.ReactNode
}

export function CodeBlock({ inline, className, children }: CodeBlockProps) {
  const [copied, setCopied] = useState(false)

  // Inline code
  if (inline) {
    return (
      <code className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    )
  }

  // Extract language from className (format: "language-javascript")
  const match = /language-(\w+)/.exec(className || '')
  const language = match ? match[1] : 'text'

  // Convert children to string
  const codeString = String(children).replace(/\n$/, '')

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-gray-700">
      {/* Header with language and copy button */}
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
        <span className="text-gray-300 text-xs font-mono uppercase">
          {language}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-gray-300 hover:text-white transition-colors text-xs"
          aria-label="Copia codice"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              <span>Copiato!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>Copia</span>
            </>
          )}
        </button>
      </div>

      {/* Code block with syntax highlighting */}
      <SyntaxHighlighter
        language={language}
        style={oneDark}
        customStyle={{
          margin: 0,
          padding: '1rem',
          fontSize: '0.875rem',
          lineHeight: '1.5',
          background: '#1e1e1e',
        }}
        wrapLongLines={false}
      >
        {codeString}
      </SyntaxHighlighter>
    </div>
  )
}
